import { Rocket, Search, ChevronDown, ChevronRight, ExternalLink, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useAccountStore } from '../../stores/useAccountStore';
import s from './Pages.module.css';

type DeployFilter = 'all' | 'custom' | 'prefab';

export function DeploymentsPage() {
  const deployments = useAccountStore(a => a.deployments);
  const [filter, setFilter] = useState<DeployFilter>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const filtered = deployments.filter(d => {
    if (filter === 'custom' && d.category === 'prefab') return false;
    if (filter === 'prefab' && d.category !== 'prefab') return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return d.projectName.toLowerCase().includes(q) || d.txHash.toLowerCase().includes(q) || d.network.toLowerCase().includes(q);
    }
    return true;
  });

  const finalized = deployments.filter(d => d.status === 'finalized').length;
  const failed = deployments.filter(d => d.status === 'failed').length;
  const prefabCount = deployments.filter(d => d.category === 'prefab').length;

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(() => {});
  };

  const explorerUrl = 'https://explorer.rivellum.network';

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Deployments</h1>
        <p className={s.pageSubtitle}>All custom code and prefab instance deployments.</p>
      </div>

      <div className={s.statsRow}>
        <div className={s.statCard}>
          <div className={s.statLabel}>Total</div>
          <div className={s.statValue}>{deployments.length}</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Finalized</div>
          <div className={s.statValue}>{finalized}</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Failed</div>
          <div className={s.statValue}>{failed}</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Prefab Instances</div>
          <div className={s.statValue}>{prefabCount}</div>
        </div>
      </div>

      {/* Filters */}
      <div className={s.searchBar}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 10, color: 'var(--color-text-tertiary)' }} />
          <input
            className={s.searchInput}
            style={{ paddingLeft: 36 }}
            placeholder="Search deployments..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className={`${s.filterChip} ${filter === 'all' ? s.filterActive : ''}`} onClick={() => setFilter('all')}>All</button>
        <button className={`${s.filterChip} ${filter === 'custom' ? s.filterActive : ''}`} onClick={() => setFilter('custom')}>Custom Code</button>
        <button className={`${s.filterChip} ${filter === 'prefab' ? s.filterActive : ''}`} onClick={() => setFilter('prefab')}>Prefab Instances</button>
      </div>

      {filtered.length === 0 ? (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><Rocket size={48} /></div>
          <div className={s.emptyTitle}>No deployments{filter !== 'all' ? ` (${filter})` : ''}</div>
          <div className={s.emptyDesc}>
            Deploy custom code from the IDE or deploy a prefab instance to see it here.
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Network</th>
                <th>Contract</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const isExpanded = expandedId === d.id;
                return (
                  <>
                    <tr
                      key={d.id}
                      className={s.tableClickable}
                      onClick={() => setExpandedId(isExpanded ? null : d.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ width: 28, paddingRight: 0 }}>
                        {isExpanded
                          ? <ChevronDown size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                          : <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                        }
                      </td>
                      <td style={{ fontWeight: 500 }}>{d.projectName}</td>
                      <td>
                        <span className={`${s.badge} ${d.category === 'prefab' ? s.badgeInfo : s.badgeNeutral}`}>
                          {d.category === 'prefab' ? 'Prefab' : 'Custom'}
                        </span>
                      </td>
                      <td>
                        <span className={`${s.badge} ${
                          d.status === 'finalized' ? s.badgeSuccess :
                          d.status === 'failed' ? s.badgeDanger : s.badgeWarning
                        }`}>
                          {d.status}
                        </span>
                      </td>
                      <td><span className={`${s.badge} ${s.badgeNeutral}`}>{d.network}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-tertiary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.contractAddress ? `${d.contractAddress.slice(0, 10)}…${d.contractAddress.slice(-8)}` : '—'}
                      </td>
                      <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {new Date(d.timestamp).toLocaleDateString()}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${d.id}-detail`}>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <div style={{
                            padding: '16px 24px 20px 42px',
                            background: 'var(--color-bg-tertiary)',
                            borderBottom: '1px solid var(--color-border)',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px 32px',
                            fontSize: 13,
                          }}>
                            {d.contractAddress && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Contract Address</span>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span>{d.contractAddress}</span>
                                  <button
                                    onClick={e => { e.stopPropagation(); copyToClipboard(d.contractAddress!, `contract-${d.id}`); }}
                                    title="Copy"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-tertiary)' }}
                                  >
                                    {copiedField === `contract-${d.id}` ? <Check size={13} /> : <Copy size={13} />}
                                  </button>
                                  <a
                                    href={`${explorerUrl}/contract/${d.contractAddress}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    title="View in Explorer"
                                    style={{ color: 'var(--color-accent)', display: 'inline-flex' }}
                                  >
                                    <ExternalLink size={13} />
                                  </a>
                                </div>
                              </div>
                            )}
                            <div>
                              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Intent ID</span>
                              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{d.txHash || '—'}</span>
                                {d.txHash && (
                                  <button
                                    onClick={e => { e.stopPropagation(); copyToClipboard(d.txHash, `tx-${d.id}`); }}
                                    title="Copy"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-tertiary)' }}
                                  >
                                    {copiedField === `tx-${d.id}` ? <Check size={13} /> : <Copy size={13} />}
                                  </button>
                                )}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Project</span>
                              <div style={{ marginTop: 4 }}>{d.projectName}</div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Deployed</span>
                              <div style={{ marginTop: 4 }}>
                                {new Date(d.timestamp).toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Network</span>
                              <div style={{ marginTop: 4 }}>{d.network}</div>
                            </div>
                            {d.registryEntry && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Registry Entry</span>
                                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 4 }}>{d.registryEntry}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
